import { useCallback, useId, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useUploadDocument } from "@/hooks/useUploadPdf";
import { InvoiceType } from "@/lib/constants/invoice-type";
import { cn } from "@/lib/utils";

type Props = {
  tenantId: string;
  clickUploadEnabled?: boolean;
  invoiceType?: InvoiceType;
  children:
    | React.ReactNode
    | ((props: { isUploading: boolean }) => React.ReactNode);
  onSuccess?: (invoiceIds: string[]) => void;
};

export const DocumentUploadDropzone = ({
  tenantId,
  clickUploadEnabled = false,
  invoiceType = InvoiceType.PAYABLE,
  children,
  onSuccess = () => {},
}: Props) => {
  const { t } = useTranslation();
  const [isFileDragged, setIsFileDragged] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { uploadDocument } = useUploadDocument({ tenantId });

  const onDragEnter = () => setIsFileDragged(true);
  const onDragLeave = () => setIsFileDragged(false);

  const handleUpload = useCallback(
    async (acceptedFiles: any) => {
      try {
        setIsUploading(true);
        setIsFileDragged(false);
        const invoiceIds = await uploadDocument({
          acceptedFiles,
          onUploadError: handleUploadError,
          invoiceType,
        });
        invoiceIds && handleSuccess(invoiceIds);
      } finally {
        setIsUploading(false);
      }
    },
    [uploadDocument],
  );

  const handleUploadError = (error: any): void => {
    const errorCode = error?.data?.code;
    const errorData = error?.data?.data;

    if (errorCode === "FRONTEND") {
      toast.error(error?.data?.message, { richColors: true });
      return;
    }

    const errMessage = errorCode
      ? (t(`server_errors.${errorCode}`, { ...errorData }) as string)
      : t("generic_error");

    toast.error(errMessage, { richColors: true });
  };

  const handleSuccess = (invoiceIds: string[]) => {
    onSuccess(invoiceIds);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleUpload,
    onDragLeave: () => setIsFileDragged(false),
    multiple: true,
  });

  const inputId = `dropzone-file-${useId()}`;

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (clickUploadEnabled) {
      e.stopPropagation();
      inputRef.current?.click();
    }
  };

  return (
    <div
      onClick={handleClick}
      onDragOver={onDragEnter}
      onDragLeave={onDragLeave}
      {...(isFileDragged ? getRootProps() : {})}
      style={{
        border: isDragActive ? "2px solid #ccc" : "",
        backgroundColor: isDragActive ? "#f8f9fa" : "",
      }}
      className="h-full"
    >
      {isFileDragged && (
        <input {...(isFileDragged ? getInputProps() : {})} />
        //can add dialog here for extra context
      )}
      <div className={cn(isDragActive ? "pointer-events-none" : "", "h-full")}>
        {typeof children === "function" ? children({ isUploading }) : children}
        <input
          id={inputId}
          type="file"
          ref={inputRef}
          accept=".png,.jpg,.jpeg,.pdf,.xml,.tif,.tiff"
          hidden
          multiple
          onChange={(event) => {
            handleUpload(event.target.files);
          }}
        />
      </div>
    </div>
  );
};
